/*
 * assets/dwc-studio-context.js
 * P4-08 | authority D-362/C-396 | implements P2-06 section 3 session-scoped studio context.
 *
 * Context may begin ONLY from an explicit customer click inside a controlled, shipped P4-06 studio journey.
 * It is NEVER created from a query parameter, referrer, browser history, URL/title text, a generic visit,
 * or a held/unknown studio. State lives in sessionStorage only (tab-scoped, per P2-06 'Context retention').
 *
 * Journey the state machine allows:
 *   /pages/<validated-studio>            -> arm studio
 *   click a class link on that page      -> arm class (exact path recorded at click time)
 *   /collections/<armed-class>           -> render context
 *   click a product link on that page    -> arm product (exact path recorded at click time)
 *   /products/<armed-product>            -> render context
 * Anything else clears state before render.
 *
 * REV2 (P4-08 QA defect IMPL-P408-01): returning to the armed class page no longer wipes the armed product.
 * P2-06 'Context retention' allows context to survive Back/Forward while the restored page is still part of
 * the same validated journey; the previous build cleared armedProduct on the class page, so Back-then-Forward
 * dropped the context bar on the product. armedProduct is now reset only when a different class is armed.
 *
 * REV3 (P4-08 QA defect OBS-P408-01): the controlled studio Page bodies mark class links
 * target="_blank" rel="noopener noreferrer". A noopener tab inherits no sessionStorage, so the default
 * customer journey opened outside the validated context. On a validated studio landing page ONLY, and for
 * ONLY the anchors the existing arming predicate already authorizes, the default navigation is normalized
 * to the same tab: target="_blank" is removed and the noopener/noreferrer tokens that existed solely for
 * that new-tab behaviour are stripped. Unrelated rel tokens are preserved, no other link is touched, no Page
 * body is modified, and modifier/middle clicks still open a new tab because the browser handles those natively.
 */
(function () {
  'use strict';
  var CFG = window.DWCStudioContextConfig;
  if (!CFG || !CFG.studios) return;

  var KEY = 'dwc:studio-context:v1';
  var STUDIOS = CFG.studios;            // { handle: 'Studio Name' } - the accepted 54-entry manifest
  var PATH = window.location.pathname.replace(/\/+$/, '') || '/';

  function read() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function write(v) {
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {}
  }
  function clear() {
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }
  function isValidStudio(h) {
    return !!(h && Object.prototype.hasOwnProperty.call(STUDIOS, h));
  }
  function norm(href) {
    if (!href) return null;
    var a = document.createElement('a');
    a.href = href;
    if (a.host !== window.location.host) return null;
    return a.pathname.replace(/\/+$/, '') || '/';   // query strings are deliberately discarded
  }

  /* REV3 | OBS-P408-01 | same-tab normalization for already-authorized controlled class anchors only.
     Runs only from the validated-studio branch below, so held, inactive, unknown and non-manifest pages
     are fail-closed and untouched. Idempotent via the data-dwc-samet marker. */
  function normalizeControlledClassLinks() {
    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (a.getAttribute('data-dwc-samet') === '1') continue;
      var p = norm(a.getAttribute('href'));
      if (!p || !/^\/collections\//.test(p)) continue;   // the exact predicate that arms a class hop
      if (a.getAttribute('target') === '_blank') { a.removeAttribute('target'); }
      var rel = a.getAttribute('rel');
      if (rel) {
        var kept = [];
        var tokens = rel.split(/\s+/);
        for (var t = 0; t < tokens.length; t++) {
          var tok = tokens[t];
          if (tok === '') continue;
          var low = tok.toLowerCase();
          if (low === 'noopener' || low === 'noreferrer') continue;
          kept.push(tok);
        }
        if (kept.length) { a.setAttribute('rel', kept.join(' ')); } else { a.removeAttribute('rel'); }
      }
      a.setAttribute('data-dwc-samet', '1');
    }
  }
  window.DWCStudioContextNormalize = normalizeControlledClassLinks;   // exposed for focused tests only

  var state = read();

  /* ---- 1. studio landing page: the only place context can begin ---- */
  var studioMatch = PATH.match(/^\/pages\/([^\/]+)$/);
  if (studioMatch && isValidStudio(studioMatch[1])) {
    if (!state || state.studio !== studioMatch[1]) {
      state = { studio: studioMatch[1], stage: 'studio', armedClass: null, armedProduct: null };
    } else {
      state.stage = 'studio';
    }
    write(state);
    normalizeControlledClassLinks();
  } else if (studioMatch) {
    /* a /pages/ route that is not a validated studio - held, unknown or generic page */
    clear(); state = null;
  } else if (state && isValidStudio(state.studio)) {
    /* ---- 2. class collection: only the exact path armed by a click on the studio page ---- */
    if (/^\/collections\//.test(PATH)) {
      if (state.armedClass === PATH) {
        state.stage = 'class'; write(state);   /* armedProduct deliberately preserved for Back/Forward */
      } else { clear(); state = null; }
    /* ---- 3. product: only the exact path armed by a click on the armed class surface ---- */
    } else if (/^\/products\//.test(PATH)) {
      if (state.armedProduct === PATH) {
        state.stage = 'product'; write(state);
      } else { clear(); state = null; }
    } else if (/^\/cart/.test(PATH)) {
      /* cart keeps state alive but renders no studio control (P2-06 s4: destination unresolved) */
    } else {
      /* any other generic destination clears (P2-06 'Context clearing') */
      clear(); state = null;
    }
  } else if (state) {
    clear(); state = null;
  }

  /* ---- arm the next hop on explicit click only ---- */
  document.addEventListener('click', function (ev) {
    var s = read();
    if (!s || !isValidStudio(s.studio)) return;
    var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
    if (!a) return;
    var p = norm(a.getAttribute('href'));
    if (!p) return;
    if (s.stage === 'studio' && /^\/collections\//.test(p)) {
      if (s.armedClass !== p) { s.armedProduct = null; }
      s.armedClass = p; write(s);
    } else if (s.stage === 'class' && /^\/products\//.test(p)) {
      s.armedProduct = p; write(s);
    }
  }, true);

  /* ---- render ---- */
  var mount = document.querySelector('[data-dwc-studio-context]');
  if (!mount) return;
  if (!state || !isValidStudio(state.studio)) return;
  if (state.stage !== 'class' && state.stage !== 'product') return;   // studio page already has its breadcrumb

  var name = STUDIOS[state.studio];
  var bar = document.createElement('div');
  bar.className = 'dwc-studio-context';
  bar.setAttribute('role', 'note');
  var span = document.createElement('span');
  span.className = 'dwc-studio-context__label';
  span.textContent = 'Shopping for ' + name;
  var link = document.createElement('a');
  link.className = 'dwc-studio-context__return';
  link.href = '/pages/' + state.studio;
  link.textContent = 'Return to ' + name;
  bar.appendChild(span);
  bar.appendChild(link);
  mount.appendChild(bar);
})();
